import React, { useMemo } from 'react'
import { omitAll } from 'lodash/fp'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { formatFilterRule } from '@/entities/column'

import { QuickFiltersBar } from '@/widgets/quick-filters-bar'

import { Header } from '@/shared/components/ui/layout'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

import { getMoMPeriods } from '../../lib/helpers'
import {
	useDashboardStore,
	useSelectDashboardFilters,
	useSelectDashboardFiltersKeys,
} from '../../lib/store'
import MetricsByBrand from '../widgets/MetricsByBrand/Widget'
import MetricsByOptimizationGroup from '../widgets/MetricsByOptimizationGroup/Widget'
import MetricsByPricingCampaign from '../widgets/MetricsByPricingCampaign/Widget'
import { WidgetWrapper } from '../WidgetWrapper'
import { useFilterFieldsConfig } from './useFilterFiledsConfig'

export default function MainDashboardMetricsSection({
	biRevisionDate,
}: {
	biRevisionDate: string
}) {
	const { t } = useTranslation()

	const commonPeriods = useMemo(
		() => getMoMPeriods(biRevisionDate),
		[biRevisionDate]
	)
	const { setFilters, filters } = useDashboardStore(
		useSelectDashboardFilters('main_metrics')
	)
	const { storedKeys, setStoredKeys } = useDashboardStore(
		useSelectDashboardFiltersKeys('main_metrics')
	)
	const { fieldsConfig, isLoading } = useFilterFieldsConfig({
		// remove fields that unsuported by BI widgets in this dashboard section
		sanitizeFields,
	})

	return (
		<section>
			<Header className='sticky top-0 z-10 items-center justify-between bg-background/50 px-4 py-2 backdrop-blur-md'>
				<div className='flex items-center'>
					<h2 className='text-lg font-bold'>
						{t('dashboard.section.metric.title')}
					</h2>
					<Tooltip>
						<TooltipTrigger asChild>
							<InfoIcon className='ml-2 size-4' />
						</TooltipTrigger>
						<TooltipContent>
							{t('widget.metric_perfomance.tooltip')}
						</TooltipContent>
					</Tooltip>
				</div>
				{isLoading ? (
					<div className='flex items-center space-x-2'>
						<Skeleton className='h-6 w-28' />
						<Skeleton className='h-6 w-20' />
						<Skeleton className='h-6 w-24' />
					</div>
				) : (
					<QuickFiltersBar
						irremovableKeys={['optimization_group', 'pos_id']}
						rules={filters}
						storedKeys={storedKeys}
						onSaveStoredKeys={(keys) => setStoredKeys('main_metrics', keys)}
						onApply={(rules) => {
							setFilters('main_metrics', rules.map(formatFilterRule))
						}}
						fieldsConfig={fieldsConfig}
						limit={7}
					/>
				)}
			</Header>
			<div className='grid grid-cols-6 gap-4 p-4'>
				<WidgetWrapper className='col-span-6 min-h-[488px]'>
					<MetricsByOptimizationGroup
						commonPeriods={commonPeriods}
						commonFilters={filters}
					/>
				</WidgetWrapper>
				<WidgetWrapper className='col-span-6 min-h-[488px]'>
					<MetricsByBrand
						commonPeriods={commonPeriods}
						commonFilters={filters}
					/>
				</WidgetWrapper>
				<WidgetWrapper className='col-span-6 min-h-[488px]'>
					<MetricsByPricingCampaign
						commonPeriods={commonPeriods}
						commonFilters={filters}
					/>
				</WidgetWrapper>
			</div>
		</section>
	)
}
const sanitizeFields = omitAll([
	'optimization_group_id',
	'pos_ids',
	'category_ids',
])
